import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { toastApiError } from "@/lib/errorMessages";
import { MessageSquare } from "lucide-react";
import { Card, Alert } from "@/components/ui";
import { BackLink, ErrorState, LoadingState } from "@/components/common";
import {
  TicketDetailHeaderCard,
  CATEGORY_CONFIG,
  ticketCategoryLabel,
  formatFullDate,
  TicketStatus,
} from "@/components/tickets";
import {
  supportTicketKeys,
  supportTicketService,
} from "@/services/supportTicketService";
import {
  StorageUploadError,
  getStorageUploadErrorMessage,
} from "@/lib/storageUpload";
import { useStorageUploadBatch } from "@/hooks/useStorageUploadBatch";
import { StorageUploadProgressBanner } from "@/components/common/StorageUploadProgressBanner";
import { useAuthStore } from "@/store/authStore";
import { NEW_SUPPORT_TICKET_PATH } from "./supportPaths";
import { Permission } from "@/types";
import {
  SupportTicketChat,
  type SupportTicketAttachment,
} from "@/components/chat";

export default function TicketDetailPage() {
  const { t } = useTranslation("support");
  const { t: tComponents } = useTranslation("components");
  const { hasPermission } = useAuthStore();
  const showPriority = hasPermission(Permission.SUPPORT_TICKETS_UPDATE_ANY);
  const { ticketId } = useParams<{ ticketId: string }>();
  const queryClient = useQueryClient();
  const { activeUpload, uploadTicketAttachments } = useStorageUploadBatch();

  const {
    data: ticket,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: supportTicketKeys.myDetail(ticketId ?? ""),
    queryFn: () => supportTicketService.getMyTicket(ticketId!),
    enabled: Boolean(ticketId),
  });

  const sendMutation = useMutation({
    mutationFn: async ({
      message,
      attachments,
    }: {
      message: string;
      attachments: SupportTicketAttachment[];
    }) => {
      const files = attachments.map((attachment) => attachment.file);
      const uploadedAttachments = await uploadTicketAttachments(
        ticketId!,
        files,
      );
      return supportTicketService.addMessageToMyTicket(ticketId!, {
        content: message,
        attachment_urls: uploadedAttachments,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: supportTicketKeys.myDetail(ticketId!),
      });
      await queryClient.invalidateQueries({
        queryKey: supportTicketKeys.lists(),
      });
      toast.success(t("detail.messageSent"));
    },
    onError: (error) => {
      if (error instanceof StorageUploadError) {
        toast.error(getStorageUploadErrorMessage(error, tComponents));
      } else {
        toastApiError(error, "generic", t("detail.sendMessageError"));
      }
    },
  });

  if (isLoading) {
    return (
      <LoadingState
        className="flex-1"
        message={t("detail.loadingTicket")}
      />
    );
  }

  if (isError || !ticket) {
    return (
      <ErrorState
        className="flex-1"
        message={t("detail.notFound")}
        action={
          <BackLink
            variant="outline"
            fallback="/support/tickets"
          >
            {t("detail.backToTickets")}
          </BackLink>
        }
      />
    );
  }

  const categoryInfo = CATEGORY_CONFIG[ticket.category];
  const visibleMessageCount = ticket.messages.filter(
    (message) => message.visibility === "PARTICIPANTS",
  ).length;
  const awaitingPartnerReply =
    ticket.status === TicketStatus.WAITING_RESPONSE;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {awaitingPartnerReply ? (
        <Alert
          variant="info"
          icon={MessageSquare}
          title={t("detail.replyNeededTitle")}
        >
          {t("detail.replyNeededDescription")}
        </Alert>
      ) : null}

      <TicketDetailHeaderCard
        backFallback="/support/tickets"
        backAriaLabel={t("detail.backToTickets")}
        ticketNumber={ticket.ticket_number}
        subject={ticket.subject}
        status={ticket.status}
        priority={ticket.priority}
        showPriority={showPriority}
        categoryEmoji={categoryInfo.emoji}
        categoryLabel={ticketCategoryLabel(ticket.category)}
        openedOnLabel={t("detail.openedOn", {
          date: formatFullDate(ticket.created_at),
        })}
      />

      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
        {activeUpload ? (
          <div className="border-b border-surface-100 p-3">
            <StorageUploadProgressBanner upload={activeUpload} />
          </div>
        ) : null}
        <SupportTicketChat
          messages={ticket.messages.map((message) => ({
            id: message.id,
            content: message.content,
            is_from_admin: message.is_from_admin,
            visibility: message.visibility,
            created_at: message.created_at,
            sender_name: message.sender_name,
            attachments: message.attachments.map((attachment) => ({
              id: attachment.id,
              file_name: attachment.file_name,
              file_url: attachment.file_url,
              file_type: attachment.file_type,
              file_size: attachment.file_size,
            })),
          }))}
          perspective="seller"
          status={ticket.status}
          isSending={sendMutation.isPending}
          onSend={async (message, attachments) => {
            await sendMutation.mutateAsync({ message, attachments });
          }}
          title={t("messageBubble.supportTeam")}
          avatarInitials="ST"
          subtitle={t("detail.messagesCount", {
            count: visibleMessageCount,
          })}
          sendPlaceholder={t("detail.messagePlaceholder")}
          onRefresh={() => void refetch()}
          isRefreshing={isFetching && !isLoading}
          closedFooter={
            ticket.status === TicketStatus.CLOSED ? (
              <div className="mx-auto max-w-md">
                <p className="text-sm font-medium text-surface-700">
                  {t("detail.resolvedTitle")}
                </p>
                <p className="mt-1 text-sm text-surface-500">
                  {t("detail.resolvedNeedHelp")}{" "}
                  <Link
                    to={NEW_SUPPORT_TICKET_PATH}
                    className="font-semibold text-primary-600 underline-offset-2 hover:text-primary-700 hover:underline"
                  >
                    {t("detail.openNewTicket")}
                  </Link>
                </p>
              </div>
            ) : undefined
          }
        />
      </Card>
    </div>
  );
}
